import { css } from 'styled-components';

const sizes = {
  // Compact phones (max 399px width)
  compactPhoneBreakpoint: 399,
  // Extra small devices (portrait phones, less than 576px)
  extraSmallBreakpoint: 575.98,
  // Small devices (landscape phones, less than 768px)
  smallBreakpoint: 767.98,
  // Medium devices (tablets, less than 992px)
  mediumBreakpoint: 991.98,
  // Large devices (desktops, less than 1200px)
  largeBreakpoint: 1199.98,
  // Extra large screens (larger desktops >= 1400)
  extraLargeBreakpoint: 1399.98,
  // Extra wide screens (larger desktops >= 2000)
  extraExtraLargeBreakpoint: 1999.98
};

const tabletDeviceSizes = {
  ipad: { minWidth: 744, maxWidth: 1048 },
  samsungTab: { minWidth: 768, maxWidth: 1280 },
  others: { minWidth: 820, maxWidth: 1180 }
};

const intermediateDesktopSizes = {
  desktop: { minWidth: 992, maxWidth: 1400 }
};

export const tabletRange = Object.keys(tabletDeviceSizes).reduce((acc, label) => {
  acc[label] = orientation => (...args) => css`
    @media (min-width: ${tabletDeviceSizes[label].minWidth}px) and (max-width: ${tabletDeviceSizes[label]
        .maxWidth}px) ${orientation && `and (orientation: ${orientation})`} {
      ${css(...args)};
    }
  `;

  return acc;
}, {});

export const otherDevices = Object.keys(tabletDeviceSizes).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media only screen and (min-width: ${tabletDeviceSizes[label].minWidth}px) and (max-width: ${tabletDeviceSizes[
        label
      ].maxWidth}px) {
      ${css(...args)};
    }
  `;

  return acc;
}, {});

export const mediaMin = Object.keys(sizes).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (max-width: ${sizes[label]}px) {
      ${css(...args)};
    }
  `;

  return acc;
}, {});

export const aboveMin = Object.keys(sizes).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (min-width: ${sizes[label]}px) {
      ${css(...args)};
    }
  `;

  return acc;
}, {});

export const intermediateRange = Object.keys(intermediateDesktopSizes).reduce((acc, label) => {
  acc[label] = orientation => (...args) => css`
    @media (min-width: ${intermediateDesktopSizes[label].minWidth}px) and (max-width: ${intermediateDesktopSizes[label]
        .maxWidth}px) {
      ${css(...args)};
    }
  `;

  return acc;
}, {});

export const mediaLandscape = (...args) => css`
  @media screen and (orientation: landscape) {
    ${css(...args)};
  }
`;

const positioned = type => ({ x = 0, y = 0, yProp = 'top', xProp = 'left' } = {}) => css`
  position: ${type};
  ${yProp}: ${y};
  ${xProp}: ${x};
`;

export const absolute = positioned('absolute');
export const fixed = positioned('fixed');
export const relative = positioned('relative');

export const shadows = ['box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.1);'];

const spaceUnit = 4;

export const colors = {
  lightest: '#ffffff',
  pearlWhite: '#fafafa',
  alabaster: '#fbfbfb',
  neutralGray: '#f7f7f7',
  wildSand: '#f6f6f6',
  concrete: '#f2f2f2',
  foam: '#f7fcfe',
  athensGray: '#f0f1f3',
  blackHaze: '#eaebec',
  mercury: '#e8e8e8',
  yellow100: '#FFF9EF',
  paleGray: '#e4eaed',
  whiteIce: '#ecf7fc',
  londonGray: '#e0e0e0',
  lightGray: '#dedede',
  blueSpirit: '#E5F6FD',
  mischka: '#d7dadf',
  polarGreen: '#e6faf5',
  lightestRed: '#ffe7e1',
  iron: '#ccd2d4',
  palidRed: '#fee2dc',
  yellowPale: '#ffeed2',
  shadyGray: '#bcc6ca',
  blueSoft: '#C2E9F8',
  sail: '#b3e6fa',
  cadetBlue: '#b2b9c2',
  bluishCyan: '#ace8ff',
  blueSky: '#94D4F3',
  silverChalice: '#a5a5a5',
  malibu: '#7fd6f7',
  manhattan: '#F8BF88',
  dustyGray: '#9b9b9b',
  blueTeal: '#66D5FF',
  greenVille: '#72D3A1',
  rajah: '#F9A868',
  coldPurple: '#b79cd6',
  geraldine: '#F7988B',
  sunshineYellow: '#f6df1b',
  gray: '#888888',
  yellowStar: '#E9A943',
  blueRiver: '#4DAAE9',
  uniBlue: '#00adef',
  uniBlueFaded: 'rgba(0, 173, 239, 0.5)',
  emerald: '#44c482',
  freshGreen: '#a3cf2a',
  gothic: '#6e909c',
  buttercup: '#F5A623',
  calmGray: '#7f7f7f',
  slateGray: '#6F8590',
  sunsetOrange: '#f29020',
  paleSky: '#6d7d86',
  glowPink: '#ff66b2',
  redPrimary: '#F87052',
  limeGreen: '#5da436',
  cornFlowerBlue: '#647a85',
  blueExclusive: '#0384B3',
  crimsonBlaze: '#eb422c',
  darkGray: '#666666',
  vibrantRuby: '#ea2039',
  magicalGreen: '#295624',
  lightFoam: '#fcfdfe',
  shuttleGray: '#545d69',
  forestGreen: '#3b7634',
  scorpion: '#585858',
  riverBed: '#4d5c64',
  casal: '#2c5464',
  blueDianne: '#224251',
  neutralized: 'rgba(34, 66, 81, 0.03)',
  shiftedGray: '#262626',
  blueBay: '#02243C',
  darkest: '#000000',
  skylineBlue: '#66d5ff',
  noColor: 'transparent',
  mistySilver: '#E4EAED',
  blueBayoux: '#3A5F6F',
  palePeach: '#FFCEC3',
  grayShadow: 'rgba(34, 66, 81, 0.2)',
  yellowGate: '#FFCC51',
  grey: '#808080',
  lightMint: '#e6fef0',
  mountainMeadow: '#14ae5c',
  redStone: '#E46A72',
  lightDianne: '#1C3947',
  japaneseIndigo: '#22425199',
  brightGray: '#efefef',
  nobel: '#959595',
  steelBlueGray: '#8D98A5',
  oceanWave: '#417E9A',
  offWhite: '#FCFCFC',
  mintCream: '#F0FEF6',
  seashellPeach: '#FFF4F2',
  frostedMint: '#F5FFF9'
};

export const flexGap = (gap, direction = 'row') => {
  if (direction !== 'row' && direction !== 'column') {
    // eslint-disable-next-line quotes
    console.error("direction must be either 'row' or 'column'");

    return '';
  }

  const isColumn = direction === 'column';

  return `
    display: flex;
    ${direction !== 'row' ? `flex-direction: ${direction};` : ''}

    > * {
      margin-${isColumn ? 'bottom' : 'right'}: ${gap}px;
    }

    > *:last-child {
      margin-${isColumn ? 'bottom' : 'right'}: 0;
    }

    @supports selector(:has(*)) {
      & {
        gap: ${gap}px;
      }

      & > * {
        margin-${isColumn ? 'bottom' : 'right'}: 0;
        ${!isColumn ? 'margin-bottom: 0;' : ''}
      }
    }
  `;
};

export const buildTheme = (colors = {}) => ({
  colors,
  space: (x = 1) => `${x * spaceUnit}px`,
  gap: (x = 1, direction = 'row') => flexGap(x * spaceUnit, direction),
  rem: px => `${px / 16}rem`,
  mapOffsetTopValue: '247px',
  ...mediaMin,
  header404: {
    background: colors.lightest,
    logo: {
      uColor: colors.uniBlue,
      restColor: colors.scorpion
    },
    linkColor: colors.shuttleGray
  },
  registrationNumber: {
    text: colors.cornFlowerBlue,
    iconColor: colors.cornFlowerBlue
  },
  button: {
    primary: {
      background: colors.uniBlue,
      color: colors.lightest,
      hover: colors.blueTeal
    },
    secondary: {
      background: colors.lightest,
      color: colors.uniBlue
    },
    disabled: {
      background: colors.shadyGray,
      color: colors.neutralGray
    },
    border: `1.5px solid ${colors.uniBlue}`
  },
  tabs: {
    indicatorColor: colors.uniBlue,
    color: colors.cadetBlue,
    selectedColor: colors.cornFlowerBlue
  },
  radioButton: {
    checked: {
      background: colors.redPrimary
    }
  },
  checkbox: {
    color: colors.paleSky,
    border: '1px solid transparent',
    boxBorder: `1px solid ${colors.cornFlowerBlue}`,
    boxShadow: `inset 0 1px 3px 0 ${colors.mercury}`,
    checked: {
      borderColor: colors.uniBlue,
      background: colors.uniBlue,
      color: colors.lightest
    },
    helperText: colors.steelBlueGray
  },
  closeButton: {
    background: colors.cadetBlue
  },
  dropdown: {
    containerColor: colors.blueDianne,
    carotColor: colors.cornFlowerBlue,
    color: colors.paleSky,
    background: colors.lightest,
    separatorColor: colors.alabaster,
    itemColor: colors.blackHaze,
    selectedColor: colors.uniBlue
  },
  slider: {
    backgroundActive: colors.uniBlue,
    background: colors.athensGray,
    buttonBackground: colors.lightest,
    buttonShadow: `0 2px 5px 0 ${colors.mercury}`
  },
  clearButton: {
    color: colors.cornFlowerBlue
  },
  dateRange: {
    color: colors.cornFlowerBlue,
    monthColor: colors.blueDianne,
    disabledColor: colors.mercury,
    selectedColor: colors.lightest,
    selectedBackground: colors.uniBlue,
    arrowBordersColor: colors.athensGray,
    pickerBoxShadow: `0 1px 8px 0 ${colors.mercury}`
  },
  filter: {
    background: colors.lightest,
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
    groupTitleColor: colors.gothic,
    color: colors.paleSky,
    checkbox: {
      border: '1px solid transparent',
      checked: {
        background: colors.whiteIce,
        borderColor: colors.uniBlue,
        color: colors.uniBlue,
        checkBackground: colors.uniBlue
      }
    },
    heading: colors.blueDianne
  },
  links: {
    color: colors.uniBlue,
    background: colors.blueSpirit
  },
  header: {
    back_to_uniplaces: {
      button_color: colors.uniBlue
    },
    back_to_residences: {
      button_color: colors.redPrimary
    },
    list_your_place: {
      button_background_color: colors.uniBlue,
      button_hover_color: colors.blueTeal
    },
    label: {
      color: colors.lightest,
      background: colors.lightest,
      selected: colors.uniBlue
    },
    link: {
      color: colors.cornFlowerBlue,
      selected: colors.uniBlue,
      signup: colors.uniBlue,
      authlistLinkColor: colors.blueDianne
    },
    separator: {
      background: colors.blackHaze
    },
    dropdown: {
      background: colors.lightest,
      border: colors.paleGray,
      icon: colors.blueDianne,
      hoverLinks: colors.wildSand
    },
    mobile: {
      closePath: colors.paleSky,
      svg: {
        pathFill: colors.uniBlue,
        gPathFill: colors.scorpion
      },
      welcome: {
        color: colors.mischka
      },
      separator: {
        borderColor: colors.shadyGray
      },
      login: {
        color: colors.uniBlue,
        background: colors.lightest
      },
      signup: {
        color: colors.lightest,
        background: colors.uniBlue
      },
      dropdown: {
        icon: colors.cornFlowerBlue
      },
      background: colors.lightest,
      hamburger: colors.blueDianne
    },
    background: colors.lightest,
    hamburger: colors.blueDianne,
    hamburgerHover: colors.wildSand,
    authLinksBackground: colors.wildSand
  },
  backgrounds: {
    lightest: colors.lightest,
    concrete: colors.concrete,
    blue: colors.blueBay,
    neutral: colors.neutralGray,
    calm: colors.calmGray,
    alabaster: colors.alabaster,
    shuttleGray: colors.shuttleGray,
    neutralized: colors.blueDianne,
    uniBlue: colors.uniBlue,
    uniBlueFaded: colors.uniBlueFaded,
    cornFlowerBlue: colors.cornFlowerBlue,
    pearlWhite: colors.pearlWhite
  },
  placeholderText: {
    iron: colors.iron
  },
  carousel: {
    background: colors.lightest
  },
  typography: {
    bodyColor: colors.darkest,
    titleColor: colors.darkest,
    invertedColor: colors.lightest,
    uniBlueTitle: colors.uniBlue,
    grayText: colors.calmGray,
    footerColor: colors.darkGray,
    footerTitle: colors.shiftedGray,
    searchBarColor: colors.paleSky,
    inputText: colors.cornFlowerBlue
  },
  interactions: {
    enhancedColor: colors.shiftedGray
  },
  borders: {
    separatorColor: colors.londonGray,
    searchBarBorder: colors.athensGray,
    inputBorder: colors.shadyGray
  },
  labels: {
    color: colors.lightest,
    newBackground: colors.coldPurple,
    exclusiveBackground: colors.uniBlue,
    trustedBackground: colors.emerald,
    instantBookingBackground: colors.rajah,
    verifiedBackground: colors.malibu,
    uniResidencesBackground: colors.redPrimary,
    uniplacesExclusiveBackground: colors.blueExclusive
  },
  paginator: {
    buttonBackground: colors.athensGray,
    textColor: colors.cornFlowerBlue,
    activePageBackground: colors.mistySilver
  },
  card: {
    background: colors.lightest,
    color: colors.paleSky,
    boxShadow: `0 1px 2px 0 ${colors.mercury}`,
    primaryTextColor: colors.blueDianne,
    secondaryTextColor: colors.dustyGray,
    neighbourhoodIconColor: colors.uniBlue,
    favouriteButtonColor: colors.geraldine,
    reviewStarFilledColor: colors.buttercup,
    reviewStarEmptyColor: colors.athensGray
  },
  sections: {
    cities: {
      primaryColor: colors.uniBlue,
      title: colors.blueDianne
    }
  },
  footer: {
    background: colors.wildSand,
    title: colors.blueDianne,
    link: colors.cornFlowerBlue,
    line: colors.shadyGray
  },
  newSwitch: {
    background: colors.paleGray,
    color: colors.lightest,
    colorSelected: colors.uniBlue,
    indicatorBackground: colors.lightest
  },
  buttons: {
    primary: {
      background: colors.uniBlue,
      color: colors.lightest,
      hover: colors.blueTeal
    },
    secondary: {
      background: colors.lightest,
      border: colors.blueTeal,
      color: colors.uniBlue,
      hover: colors.blueSpirit
    },
    tertiary: {
      background: colors.lightest,
      border: colors.shadyGray,
      color: colors.cornFlowerBlue,
      hover: colors.paleGray,
      selected: colors.blueDianne,
      selectedColor: colors.blueDianne,
      backgroundSelected: colors.paleGray
    },
    danger: {
      background: colors.blueDianne,
      color: colors.lightest,
      hover: colors.cornFlowerBlue
    },
    ghost: {
      background: colors.lightest,
      color: colors.cornFlowerBlue,
      hover: colors.wildSand
    }
  },
  range: {
    barBackground: colors.athensGray,
    barBackgroundActive: colors.uniBlue,
    buttonBackground: colors.lightest,
    buttonBackgroundActive: colors.uniBlue,
    buttonBoxShadow: `0 2px 5px 0 ${colors.mercury}`,
    buttonBorder: `1px solid ${colors.mercury}`,
    valuesBorder: `1px solid ${colors.shadyGray}`,
    valuesBorderFocused: `1px solid ${colors.shiftedGray}`
  },
  histogram: {
    fill: colors.blueSoft,
    outsideFill: colors.athensGray
  },
  modal: {
    header: {
      title: colors.blueDianne,
      border: colors.concrete
    },
    text: colors.blueDianne
  },
  wishlistModal: {
    textColor: colors.slateGray
  },
  createOfferAlertModal: {
    primaryTextColor: colors.slateGray,
    secondaryTextColor: colors.paleSky,
    inputBackgroundColor: colors.neutralGray,
    inputBorderColor: colors.blackHaze,
    linksColor: colors.uniBlue,
    bellIconStrokeColorMobile: colors.uniBlue
  },
  toast: {
    title: colors.uniBlue,
    info: {
      background: colors.polarGreen
    }
  },
  selectMoveIn: {
    backgroundColor: colors.blueSoft,
    color: colors.blueDianne,
    dayColor: colors.blueDianne,
    monthColor: colors.blueBayoux,
    arrowColor: colors.blueDianne
  },
  dynamicCard: {
    textColor: colors.lightest,
    buttonBackground: colors.lightest,
    addDatesBackground: colors.uniBlue,
    addDatesButtonBorder: `1px solid ${colors.uniBlue}`,
    addDatesButtonTextColor: colors.uniBlue,
    addDatesCalendarIconColor: colors.uniBlue,
    createAlertBackground: colors.coldPurple,
    createAlertButtonBorder: `1px solid ${colors.coldPurple}`,
    createAlertButtonTextColor: colors.coldPurple,
    createAlertIconColor: colors.coldPurple
  },
  trustBanner: {
    textColor: colors.glowPink,
    backgroundColor: colors.glowPink,
    borderColor: colors.glowPink
  },
  accordion: {
    background: colors.wildSand,
    title: colors.darkest,
    text: colors.cornFlowerBlue,
    urlText: colors.uniBlue,
    chevron: colors.cornFlowerBlue
  },
  unisystem: {
    button: {
      primary: {
        background: colors.uniBlue,
        color: colors.lightest,
        hover: colors.blueTeal
      },
      secondary: {
        background: colors.lightest,
        color: colors.uniBlue,
        border: colors.blueTeal,
        hover: colors.blueSpirit
      },
      tertiary: {
        background: colors.lightest,
        color: colors.blueDianne,
        border: colors.shadyGray,
        hover: colors.paleGray,
        selected: colors.blueDianne
      },
      danger: {
        background: colors.blueDianne,
        color: colors.lightest,
        hover: colors.cornFlowerBlue
      },
      ghost: {
        background: 'transparent',
        color: colors.cornFlowerBlue,
        hover: colors.wildSand
      },
      disabled: {
        background: colors.shadyGray,
        color: colors.lightest
      },
      border: `1.5px solid ${colors.uniBlue}`
    },
    input: {
      insideLabel: colors.cornFlowerBlue,
      label: colors.blueDianne,
      border: colors.shadyGray,
      text: colors.blueDianne,
      placeholder: colors.shadyGray,
      helperLabel: colors.shadyGray,
      disabled: {
        background: colors.wildSand,
        text: colors.shadyGray,
        backgroundDefault: colors.lightest
      },
      focused: {
        background: colors.foam,
        border: colors.blueDianne,
        text: colors.blueDianne
      },
      filled: {
        text: colors.cornFlowerBlue,
        background: colors.lightest
      },
      success: {
        icon: colors.blueTeal,
        border: colors.blueTeal
      },
      error: {
        icon: colors.geraldine,
        border: colors.geraldine
      },
      errorSystem: {
        background: colors.blueDianne,
        color: colors.lightest,
        iconColor: colors.lightest
      },
      message: {
        text: colors.cornFlowerBlue,
        errorColor: colors.redPrimary,
        successColor: colors.emerald
      },
      info: {
        text: colors.lightest,
        background: colors.blueDianne
      },
      maxLength: {
        text: colors.cornFlowerBlue,
        textLimit: colors.geraldine
      }
    },
    tab: {
      color: colors.blueDianne,
      border: {
        default: colors.shadyGray,
        hover: colors.shadyGray,
        selected: colors.blueTeal
      },
      background: {
        hover: colors.neutralGray
      }
    },
    checkbox: {
      label: colors.blueDianne,
      check: colors.blueDianne,
      background: colors.softestNeutralGray,
      disabled: {
        border: colors.shadyGray,
        background: colors.shadyGray,
        check: colors.shadyGray,
        text: colors.shadyGray
      }
    },
    radioButton: {
      label: colors.blueDianne,
      border: colors.blueDianne,
      check: colors.blueDianne,
      disabled: {
        border: colors.shadyGray,
        background: colors.wildSand,
        check: colors.shadyGray
      }
    },
    progressStepper: {
      completedStep: {
        background: colors.blueTeal,
        text: colors.blueDianne
      },
      activeStep: {
        background: colors.blueDianne,
        text: colors.blueDianne
      },
      futureStep: {
        background: colors.shadyGray,
        text: colors.shadyGray
      },
      stepNumber: {
        text: colors.lightest,
        icon: colors.lightest
      }
    },
    statusBanner: {
      success: {
        icon: colors.blueTeal,
        background: colors.blueSpirit
      },
      error: {
        background: colors.lightestRed
      },
      background: {
        yellowLight: colors.yellowPale,
        red: colors.lightestRed,
        mint: colors.lightMint,
        blue: colors.blueSpirit,
        yellow: colors.yellow100,
        green: colors.lightMint,
        white: colors.lightest
      },
      text: {
        green: colors.mountainMeadow,
        default: colors.blueDianne
      },
      close: {
        icon: colors.cornFlowerBlue
      }
    },
    pill: {
      color: colors.blueDianne,
      background: {
        blue: colors.sail,
        red: colors.lightestRed,
        yellow: colors.yellowPale,
        grey: colors.paleGray,
        green: colors.emerald,
        uniBlue: colors.uniBlue
      }
    },
    statusCard: {
      color: {
        text: colors.blueDianne,
        pill: colors.lightest
      },
      background: {
        pill: colors.redPrimary,
        red: colors.lightestRed
      }
    },
    dropdown: {
      label: colors.blueDianne,
      border: colors.shadyGray,
      text: colors.blueDianne,
      selected: {
        border: colors.blueDianne,
        background: colors.foam
      },
      list: {
        border: colors.paleGray,
        text: colors.cornFlowerBlue,
        background: colors.lightest,
        hover: {
          background: colors.wildSand
        }
      },
      disabled: {
        background: colors.wildSand,
        text: colors.shadyGray,
        arrow: colors.shadyGray
      }
    },
    date: {
      borderColor: colors.shadyGray,
      navigation: {
        hoverBackground: colors.paleGray,
        dropdownText: colors.blueDianne
      },
      calendar: {
        days: {
          hoverBackground: colors.blueSoft,
          outsideColor: colors.paleGray,
          selectedColor: colors.uniBlue
        }
      }
    },
    dragAndDropBox: {
      border: colors.shadyGray,
      title: colors.blueDianne,
      subtitle: colors.shadyGray,
      background: {
        dragOver: colors.neutralGray
      },
      badge: {
        icon: colors.cornFlowerBlue,
        background: colors.wildSand,
        text: colors.blueDianne
      },
      uploadColor: colors.lightBlue
    },
    chip: {
      color: colors.blueDianne,
      border: {
        default: colors.shadyGray,
        selected: colors.blueDianne,
        primary: colors.blueTeal
      },
      background: {
        default: colors.lightest,
        hover: colors.neutralGray,
        selected: colors.paleGray,
        primary: colors.lightest
      }
    },
    arrowLink: {
      color: colors.uniBlue
    },
    calendar: {
      background: colors.lightest,
      shadow: colors.grayShadow,
      borderColor: colors.shadyGray,
      arrowButtonsBackground: colors.wildSand,
      selectedBetween: colors.lightBlue,
      selectedBetweenBackground: colors.blueSpirit,
      disabledDatesColor: colors.shadyGray,
      color: colors.blueDianne,
      hoverBackground: colors.blueTeal,
      datesLabel: {
        titleColor: colors.cornFlowerBlue,
        selectMoveInOut: {
          color: colors.blueDianne,
          background: colors.blueSpirit
        }
      },
      selected: {
        background: colors.uniBlue,
        color: colors.lightest,
        between: {
          color: colors.uniBlue
        }
      },
      buttonSelectDates: {
        color: colors.redPrimary,
        borderColor: colors.redPrimary
      },
      buttonClearDates: {
        color: colors.uniBlue
      },
      error: {
        backgroundColor: colors.yellowStar,
        color: colors.lightest
      },
      warning: {
        backgroundColor: colors.yellowStar
      },
      talkToAnAgent: {
        color: colors.uniBlue
      },
      maxStay: {
        color: colors.cornFlowerBlue
      },
      warningBanner: {
        defaultColor: colors.blueDianne,
        defaultBackground: colors.lightest,
        instantBooking: {
          background: colors.blueSpirit
        },
        availabilityRules: {
          background: colors.yellowPale
        },
        minimumStay: {
          background: colors.lightestRed
        },
        minimumStayPenalty: {
          background: colors.yellowPale
        },
        dateChange: {
          background: colors.lightMint
        },
        extraTenantPrice: {
          background: colors.blueSpirit
        },
        fixedContractOption: {
          background: colors.yellow100,
          color: colors.blueDianne,
          arrowSvgColor: colors.blueTeal,
          instalmentsTitle: colors.cornFlowerBlue,
          border: colors.paleGray,
          instalmentColor: colors.cornFlowerBlue,
          selectedBackground: colors.blueSpirit,
          selectedBorder: colors.cornFlowerBlue,
          itemBackground: colors.lightest,
          hoverBackground: colors.foam
        }
      },
      fixedContracts: {
        background: colors.yellow100,
        color: colors.blueDianne,
        arrowSvgColor: colors.blueTeal,
        arrowSvgColorSelected: colors.blueDianne,
        instalmentColor: colors.cornFlowerBlue,
        instalmentsTitle: colors.cornFlowerBlue,
        instalmentsTitleSelected: colors.blueDianne,
        instalmentsDatesSelected: colors.blueDianne,
        selectedBackground: colors.blueSpirit,
        selectedBorder: colors.cornFlowerBlue,
        hoverBackground: colors.foam,
        itemBackground: colors.lightest,
        border: colors.paleGray
      }
    },
    placeholder: {
      background: colors.brightGray,
      text: colors.nobel
    }
  },
  progress: {
    base: colors.wildSand,
    bar: colors.bluishCyan,
    successful: colors.uniBlue,
    error: colors.redPrimary,
    errorMessage: colors.lightest
  },
  separator: {
    color: colors.shadyGray
  },
  locationSearch: {
    background: colors.lightest,
    inputColor: colors.paleSky,
    caretColor: colors.uniBlue,
    placeholderColor: colors.mercury,
    resultColor: colors.blueDianne,
    selectedBackground: colors.sail,
    resultsColorPlaceholder: colors.blueDianne
  },
  youtubePlayer: {
    progressionBar: {
      borderColor: colors.blueDianne
    }
  },
  readMoreInline: {
    color: colors.blueDianne,
    iconColor: colors.cornFlowerBlue
  },
  availabilitySection: {
    description: {
      color: colors.blueDianne
    },
    legends: {
      available: colors.bluishCyan
    }
  },
  simpleCarousel: {
    navigationControls: {
      backgroundColor: colors.wildSand,
      svgColor: colors.blueDianne
    }
  },
  reviews: {
    text: colors.blueDianne,
    details: colors.cornFlowerBlue,
    star: {
      on: colors.yellowStar,
      off: colors.shadyGray
    }
  },
  energyCertification: {
    backgrounds: {
      gradeAPlus: colors.forestGreen,
      gradeA: colors.forestGreen,
      gradeB: colors.limeGreen,
      gradeBMinus: colors.limeGreen,
      gradeC: colors.freshGreen,
      gradeD: colors.sunshineYellow,
      gradeE: colors.sunsetOrange,
      gradeF: colors.crimsonBlaze,
      gradeG: colors.vibrantRuby
    },
    modal: {
      textColor: colors.blueDianne,
      secondaryTextColor: colors.cornFlowerBlue
    },
    separator: {
      borderColor: colors.paleGray
    }
  },
  breadcrumbs: {
    text: {
      color: colors.uniBlue
    },
    lastItem: {
      color: colors.shadyGray
    }
  },
  paymentSummary: {
    info: colors.cornFlowerBlue,
    text: colors.blueDianne,
    total: colors.cornFlowerBlue,
    accordion: {
      title: colors.blueDianne,
      text: colors.blueDianne,
      chevron: colors.blueDianne
    },
    uniplacesBreakdown: {
      background: colors.foam,
      title: colors.blueDianne,
      text: colors.blueDianne,
      futurePayments: colors.cornFlowerBlue,
      included: {
        iconBackground: colors.mountainMeadow,
        icon: colors.lightest,
        text: colors.cornFlowerBlue
      },
      description: {
        color: colors.cornFlowerBlue
      }
    }
  },
  moveInOutPicker: {
    background: colors.pearlWhite
  },
  fileSizeExceeded: {
    text: colors.blueDianne
  }
});

export const theme = buildTheme(colors);
